import { Button, FlashbarProps, TextContent } from "@amzn/awsui-components-react";
import { GetCatalogErrorContent } from "common/constants";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import { retrieveStageFromHostname } from "configuration/config";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { NavigateOnCreatedOrder } from "./useCreateOrder";

const getStringPath = "common.component.LocalItOrderConfirmationContent.orderSubmitted";
const getStringPathLinks = "links";
const getFileErrorStringPath = `${getStringPath}.fileUploadError`;

/**
 * Custom hook that handles order response display by generating success messages with order details and navigation links
 *
 * @param {function} generateMessage - Function that takes a FlashbarProps.MessageDefinition and returns a string for displaying messages
 * @return {void} - This hook doesn't return anything, it performs side effects
 */
const useOrderResponse = (
    generateMessage: (message: FlashbarProps.MessageDefinition, autoScroll: boolean) => string,
) => {
    const location = useLocation();
    const navigate = useNavigate();

    const { order }: NavigateOnCreatedOrder = location.state || {};

    /**
     * Generates a hardware order link URL based on current location and order ID
     *
     * @return {string} - The generated hardware order link URL
     */
    const generateLink = () => {
        const stage = retrieveStageFromHostname();

        return getString("hardwareOrderLink", getStringPathLinks, {
            stage: stage ? "." + stage : "",
            taxonomyId: encodeURIComponent(order.taxonomyId ?? ""),
        });
    };

    useEffect(() => {
        if (order) {
            generateMessage(
                {
                    type: "success",
                    header: getString(`${getStringPath}.header`),
                    content: (
                        <TextContent>
                            {getString("initialMessage", getStringPath, { orderId: order.reference })}
                        </TextContent>
                    ),
                    action: (
                        <Button href={generateLink()} iconAlign="right" iconName="external" target="_blank">
                            {getString(`${getStringPath}.linkLabel`)}
                        </Button>
                    ),
                    dismissible: true,
                },
                true,
            );

            if (order.fileUploadFailed) {
                generateMessage(
                    {
                        type: "error",
                        header: getString(`${getFileErrorStringPath}.header`),
                        content: (
                            <TextContent>
                                {getString("content", getFileErrorStringPath, {
                                    fileNames: order.failedFiles.join(", "),
                                })}{" "}
                                {GetCatalogErrorContent("inverted")}
                            </TextContent>
                        ),
                        action: (
                            <Button href={generateLink()} iconAlign="right" iconName="external" target="_blank">
                                {getString(`${getStringPath}.linkLabel`)}
                            </Button>
                        ),
                        dismissible: true,
                    },
                    true,
                );
            }

            navigate(location.pathname, { replace: true });
        }
    }, [order, navigate, location.pathname]);
};

export default useOrderResponse;
